import * as React from 'react'
import { Outlet, useSearchParams } from 'react-router-dom'

import { Box, useToggle } from '@factorialco/deprecated-design-system'
import { ReadReportsSharePoliciesQuery } from 'generated/resources/graphql'
import { isEmpty } from 'lodash'

import { observer } from 'mobx-react'

import type { Back } from 'modules/core/components/Card/types'
import CrashLogo from 'modules/core/components/CrashLogo'
import ErrorBoundary from 'modules/core/components/ErrorBoundary'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import buildVariablesForms from 'modules/reports/lib/buildVariablesForms'
import conditions from 'modules/reports/store/collections/conditions'
import filters from 'modules/reports/store/collections/filters'
import projections from 'modules/reports/store/collections/projections'
import reports from 'modules/reports/store/collections/reports'
import { ReportShares } from 'modules/reports/store/collections/reports_shares'
import type DashboardModel from 'modules/reports/store/models/Dashboard'

import { ADD_FILTER_ACTION } from '../DashboardHeader'
import Content from './components/Content'
import { VariableValueForm } from './components/Content/DashboardFilters/variableValueForm'
import { ShareReportsPolicy } from './types'

interface PageContentProps {
  dashboard: DashboardModel
  filtersValues: VariableValueForm[]
  showAddFilterModal: boolean
  setShowAddFilterModal: (value: boolean) => () => void
  reportsSharePolicy: ShareReportsPolicy[]
}

const PageContent = ({
  dashboard,
  filtersValues,
  showAddFilterModal,
  setShowAddFilterModal,
  reportsSharePolicy,
}: PageContentProps) => {
  return (
    <Box width="full">
      <ErrorBoundary>
        <Content
          dashboard={dashboard}
          filtersValues={filtersValues}
          showAddFilterModal={showAddFilterModal}
          setShowAddFilterModal={setShowAddFilterModal}
          reportsSharePolicy={reportsSharePolicy}
        />
      </ErrorBoundary>
    </Box>
  )
}

interface Props {
  back: Back
  loading: boolean
  dashboard: DashboardModel | undefined
  shareReportsPolicies?: ReadReportsSharePoliciesQuery
}

const DashboardShow = ({ loading, dashboard, shareReportsPolicies }: Props) => {
  const [hasFailedFetching, setHasFailedFetching] = React.useState(false)
  const [search, _] = useSearchParams()
  const showModalFilterByParams = search.get('action') == ADD_FILTER_ACTION
  const reportsSharePolicy: ShareReportsPolicy[] = !isEmpty(shareReportsPolicies)
    ? shareReportsPolicies.reports.reportsConnection.edges.map(({ node }) => node)
    : []

  const dashboardId = dashboard?.id

  const { loading: loadingReport } = useAsyncData(async () => {
    if (!dashboardId) return

    const data = {
      data: {
        dashboard_id: dashboardId,
      },
    }

    try {
      await Promise.all([
        reports.fetch(data),
        projections.fetch(data),
        filters.fetch(data),
        conditions.fetch(data),
        ReportShares.fetch(),
      ])
    } catch {
      setHasFailedFetching(true)
    }
  }, [dashboardId])

  const isLoading = loading || loadingReport
  const filtersValues = React.useMemo(
    () => buildVariablesForms(dashboard?.variables || []),
    [dashboard?.variables]
  )

  const [showAddFilterModal, setShowAddFilterModal] = useToggle(false)

  if (hasFailedFetching) {
    return <CrashLogo compact />
  }

  return (
    <>
      <ResponsivePage.Body error={undefined} loading={isLoading} variant="full" showHeader={false}>
        {dashboard && (
          <PageContent
            dashboard={dashboard}
            filtersValues={filtersValues}
            setShowAddFilterModal={setShowAddFilterModal}
            showAddFilterModal={showAddFilterModal || showModalFilterByParams}
            reportsSharePolicy={reportsSharePolicy}
          />
        )}
      </ResponsivePage.Body>
      <Outlet context={{ dashboard }} />
    </>
  )
}

export default observer(DashboardShow)
